// Profile tab: avatar, username, orders preload hook
import { getTg } from '../../core/telegram.js';
import { loadMyOrders } from '../orders/list.js';
import type { ProfileAvatarCacheEntry } from '../../types/api.js';

const PROFILE_AVATAR_CACHE_KEY = 'profileAvatarCompressedV3';
const PROFILE_AVATAR_REFRESH_MS = 12 * 60 * 60 * 1000;

function readAvatarCache(
  uid: string,
  photoUrl: string,
  nowTs: number
): string {
  try {
    const raw = localStorage.getItem(PROFILE_AVATAR_CACHE_KEY) || '';
    if (!raw) return '';
    const parsed = JSON.parse(raw) as ProfileAvatarCacheEntry;
    const savedAt = Number(parsed?.savedAt ?? 0);
    const sameSource = String(parsed?.sourceUrl ?? '') === photoUrl;
    const isFresh = savedAt > 0 && nowTs - savedAt < PROFILE_AVATAR_REFRESH_MS;
    if (
      parsed &&
      typeof parsed === 'object' &&
      String(parsed.userId ?? '') === uid &&
      parsed.data &&
      parsed.data.length > 50 &&
      sameSource &&
      isFresh
    ) {
      return String(parsed.data);
    }
  } catch {
    /* ignore */
  }
  return '';
}

function profileAvatarProxySrc(url: string, nowTs: number): string {
  if (!url) return '';
  const bucket = Math.floor(nowTs / PROFILE_AVATAR_REFRESH_MS);
  return '/api/proxy-image?url=' + encodeURIComponent(url) + '&cb=' + String(bucket);
}

function applyProfileAvatarLoaded(
  avatarImg: HTMLImageElement,
  avatarEl: HTMLElement
): void {
  avatarImg.classList.add('profile-avatar-img-visible');
  avatarEl.classList.add('profile-avatar-fallback-hidden');
}

function bindProfileAvatarImage(
  avatarImg: HTMLImageElement,
  avatarEl: HTMLElement,
  src: string
): void {
  avatarImg.onload = () => {
    applyProfileAvatarLoaded(avatarImg, avatarEl);
  };
  avatarImg.onerror = () => {
    avatarImg.classList.remove('profile-avatar-img-visible');
    avatarEl.classList.remove('profile-avatar-fallback-hidden');
  };
  avatarImg.src = src;
  if (avatarImg.complete && avatarImg.naturalWidth > 0) {
    applyProfileAvatarLoaded(avatarImg, avatarEl);
  }
}

export function updateProfileView(): void {
  const avatarEl = document.getElementById('profile-avatar');
  const usernameEl = document.getElementById('profile-username');
  const avatarImg = document.getElementById('profile-avatar-img') as HTMLImageElement | null;
  if (!avatarEl || !usernameEl || !avatarImg) return;
  const tg = getTg();
  const user = tg?.initDataUnsafe?.user ?? null;
  const name =
    user && (user.first_name || user.last_name)
      ? String((user.first_name || '') + ' ' + (user.last_name || '')).trim()
      : '';
  const display = name || 'Пользователь';
  usernameEl.textContent = display;
  const letter = display[0] ? display[0].toUpperCase() : 'U';
  avatarEl.textContent = letter;

  const photoUrl = user?.photo_url ? String(user.photo_url) : '';
  const nowTs = Date.now();
  const uid = user?.id != null ? String(user.id) : '';
  const cached = readAvatarCache(uid, photoUrl, nowTs);

  if (cached) {
    bindProfileAvatarImage(avatarImg, avatarEl, cached);
  } else if (photoUrl) {
    bindProfileAvatarImage(avatarImg, avatarEl, profileAvatarProxySrc(photoUrl, nowTs));
  } else {
    avatarImg.removeAttribute('src');
    avatarImg.classList.remove('profile-avatar-img-visible');
    avatarEl.classList.remove('profile-avatar-fallback-hidden');
  }

  try {
    loadMyOrders();
  } catch {
    /* ignore */
  }
}

export async function preloadProfileAvatar(): Promise<void> {
  try {
    const tg = getTg();
    const user = tg?.initDataUnsafe?.user ?? null;
    const photoUrl = user?.photo_url ? String(user.photo_url) : '';
    if (!photoUrl) return;
    const uid = user?.id != null ? String(user.id) : '';
    const nowTs = Date.now();

    const cached = readAvatarCache(uid, photoUrl, nowTs);
    if (cached) return;

    const src = '/api/proxy-image?url=' + encodeURIComponent(photoUrl) + '&cb=' + String(nowTs);
    const res = await fetch(src, { cache: 'no-store' });
    if (!res.ok) return;
    const blob = await res.blob();
    let bitmap: ImageBitmap | null = null;
    try {
      bitmap = await createImageBitmap(blob);
    } catch {
      return;
    }

    const max = 160;
    const w = bitmap.width || max;
    const h = bitmap.height || max;
    const scale = Math.min(1, max / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(bitmap, 0, 0, cw, ch);

    let dataUrl = '';
    try {
      dataUrl = canvas.toDataURL('image/jpeg', 0.78);
    } catch {
      return;
    }
    if (!dataUrl || dataUrl.length < 50) return;
    try {
      const payload = JSON.stringify({
        userId: uid,
        sourceUrl: photoUrl,
        savedAt: nowTs,
        data: dataUrl
      } satisfies ProfileAvatarCacheEntry);
      localStorage.setItem(PROFILE_AVATAR_CACHE_KEY, payload);
      try {
        localStorage.removeItem('profileAvatarCompressedV2');
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore */
    }

    updateProfileView();
  } catch {
    /* ignore */
  }
}
